/**
 * An assistant's changes to the log (#72), as the log shows them: a
 * "Changed by Claude" line under an edited row, and a row that can put back
 * an entry an assistant removed. The server lists only the changes that are
 * still the newest change to their entry (`GET /entries/changes`); this file
 * decides where each one shows and how it reads. Pure, so it is tested
 * directly (tests/revisions.test.ts).
 */
import type { BottleUnit, WeightUnit } from "@babia/checks";
import { assistantName } from "./assistants.ts";
import { entryDetail, type EntryLike, entryName } from "./entry-text.ts";
import { clock, isSameLocalDay, weekdayDay } from "./time.ts";

/** How far back the log asks for changes, and how long a removed row stays. */
export const CHANGE_WINDOW_MS = 7 * 24 * 60 * 60 * 1000;

/** An assistant's edit or delete that is still the newest change to its entry. */
export interface EntryChange {
  id: string;
  entryId: string;
  babyId: string;
  kind: "edit" | "delete";
  /** The assistant's registered name; null when it has none. */
  clientName: string | null;
  /** When the assistant made the change. */
  at: Date;
  before: EntryLike;
  /** Null for a delete. */
  after: EntryLike | null;
}

/** Who made a change: "Claude", or "An AI assistant" with no name. */
export function changerName(change: Pick<EntryChange, "clientName">): string {
  return assistantName({ name: change.clientName });
}

/** The edits that mark a row in the log, by entry id. */
export function marksByEntry(changes: readonly EntryChange[]): Map<string, EntryChange> {
  return new Map(changes.filter((c) => c.kind === "edit").map((c) => [c.entryId, c]));
}

/**
 * A baby's entries that an assistant removed in the last week, newest entry
 * first. Each shows in the log at the removed entry's time, but only from
 * `shownSince` on: the time the timeline's loaded entries reach back to. An
 * entry put back there then shows again with the next refresh.
 */
export function removedRows(
  changes: readonly EntryChange[],
  babyId: string,
  now: Date,
  shownSince: Date,
): EntryChange[] {
  return changes
    .filter(
      (c) =>
        c.kind === "delete" &&
        c.before.babyId === babyId &&
        now.getTime() - c.at.getTime() <= CHANGE_WINDOW_MS &&
        c.before.at.getTime() >= shownSince.getTime(),
    )
    .sort((a, b) => b.before.at.getTime() - a.before.at.getTime());
}

/**
 * The entries to show beside the removed rows: an older page refreshes every
 * five minutes, so it can still hold an entry an assistant removed. The
 * removed row replaces it.
 */
export function withoutRemoved<E extends { id: string }>(
  entries: readonly E[],
  removed: readonly EntryChange[],
): E[] {
  const gone = new Set(removed.map((c) => c.entryId));
  return entries.filter((e) => !gone.has(e.id));
}

/** "8:26 am" today; "Thu 15, 8:26 am" on another day. */
export function whenText(d: Date, now: Date): string {
  return isSameLocalDay(d, now) ? clock(d) : `${weekdayDay(d)}, ${clock(d)}`;
}

/**
 * One side of a change in the sheet: "4:59 am · 4 oz — Similac". With
 * `babyName` (an edit that moved the entry to the other baby), the baby
 * first. With `named` (an edit that changed what the entry is, such as a
 * note's kind), the entry's name before its detail.
 */
export function entryLine(
  e: EntryLike,
  unit: BottleUnit,
  weightUnit: WeightUnit,
  now: Date,
  { babyName, named = false }: { babyName?: string; named?: boolean } = {},
): string {
  const detail = entryDetail(e, unit, { unit: weightUnit });
  return [babyName, whenText(e.at, now), named ? entryName(e) : undefined, detail]
    .filter(Boolean)
    .join(" · ");
}

/** "Claude changed this bottle at 8:26 am.": the sheet's first line ("removed" for a delete). */
export function changeIntro(change: EntryChange, now: Date): string {
  const what = entryName(change.after ?? change.before).toLowerCase();
  const did = change.kind === "delete" ? "removed" : "changed";
  return `${changerName(change)} ${did} this ${what} at ${whenText(change.at, now)}.`;
}

/** A time that a narrow row never breaks: "8:26 am", "Thu 15, 8:26 am". */
const unbroken = (text: string) => text.replaceAll(" ", " ");

/** "Changed by Claude · 8:26 am": the line under an edited row. */
export function markText(change: EntryChange, now: Date): string {
  return `Changed by ${changerName(change)} · ${unbroken(whenText(change.at, now))}`;
}

/** "Removed by Claude · 8:26 am": the second line of a removed row. */
export function removedText(change: EntryChange, now: Date): string {
  return `Removed by ${changerName(change)} · ${unbroken(whenText(change.at, now))}`;
}
